import React, { useEffect, useState } from 'react';
import { Button } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEdit, faTrash, faChevronLeft, faChevronRight, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { hasUserPermission } from '../utils/userPermissions';
import QuoteProductsSummary from './QuoteProductsSummary';
import './QuoteDetailProductsPanel.css';

const formatDate = value => value ? new Date(value).toLocaleDateString() : '';
const formatAmount = value => value || value === 0
  ? '₹ ' + Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })
  : '';

const DetailRow = ({ label, value }) => (
  <div className="quote-detail-row">
    <div className="quote-detail-label">{label}</div>
    <div className="quote-detail-value">{value || value === 0 ? value : '--'}</div>
  </div>
);

export default function PoSaleDetailSidebar({
  record,
  show,
  onClose,
  onEdit,
  onDelete,
  canEdit = false,
  canDelete = false,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  deleting = false,
}) {
  const [showProducts, setShowProducts] = useState(false);
  const recordId = record?.id;
  useEffect(() => { setShowProducts(false); }, [recordId, show]);
  useEffect(() => {
    if (!show || !showProducts) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setShowProducts(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [show, showProducts]);

  const quote = record?.quote || {};
  const productsOpen = show && showProducts && recordId != null;

  return (
    <>
      <div className={`quote-detail-backdrop ${show ? 'show' : ''}`} onClick={onClose} />
      <div className={`quote-detail-panel ${show ? 'show' : ''} ${productsOpen ? 'with-products' : ''}`}>
        {record && (
          <>
            <div className="quote-detail-header">
              <h5 className="mb-0">PO Sales Details</h5>
              <div className="d-flex align-items-center">
                <Button variant="outline-secondary" size="sm" className="me-2" disabled={!hasPrevious}
                  onClick={onPrevious} title="Previous PO">
                  <FontAwesomeIcon icon={faChevronLeft} className="me-1" />Prev
                </Button>
                <Button variant="outline-secondary" size="sm" className="me-3" disabled={!hasNext}
                  onClick={onNext} title="Next PO">
                  Next<FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                </Button>
                <Button variant="link" className="p-0 text-dark" aria-label="Close PO Sales details" onClick={onClose}>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            </div>
            <div className="quote-detail-body">
              <DetailRow label="PO Number" value={record.po_number} />
              <DetailRow label="Quote No." value={record.quote_id && hasUserPermission('quotes', 'view') && (record.offer_no || record.system_quote_no) ? (
                // Opens the Quote Listing with this quote's detail panel already open.
                <Link to="/quotes" state={{ openQuoteId: record.quote_id }} className="po-quote-link" title="Open this quote">{record.offer_no || record.system_quote_no}</Link>
              ) : (record.offer_no || record.system_quote_no)} />
              <DetailRow label="System Quote No." value={record.system_quote_no} />
              <DetailRow label="Customer" value={quote.customer?.name} />
              <DetailRow label="Company" value={quote.company?.name} />
              <DetailRow label="Branch" value={quote.branch?.name} />
              <DetailRow label="Principal" value={quote.principal?.name} />
              <DetailRow label="Category" value={quote.category?.name} />
              <DetailRow label="Sales Executive" value={quote.sales_executive?.name} />
              <div className="quote-detail-row">
                <div className="quote-detail-label">Products</div>
                <Button type="button" variant="outline-primary" size="sm"
                  aria-expanded={productsOpen}
                  onClick={() => setShowProducts(true)}>
                  <FontAwesomeIcon icon={faBoxOpen} className="me-2" />View Products
                </Button>
              </div>
              <DetailRow label="Quantity" value={record.qty} />
              <DetailRow label="Quoted Total" value={formatAmount(record.total)} />
              <DetailRow label="PO Amount" value={formatAmount(record.po_amount)} />
              <DetailRow label="PO Release Date" value={formatDate(record.po_release_date)} />
              <DetailRow label="PO Received Date" value={formatDate(record.po_received_date)} />
              <DetailRow label="Delivery Due Date" value={formatDate(record.delivery_due_date)} />
              <DetailRow label="PO to Principal/Vendor" value={record.po_to_principal_vendor} />
              {['Yes', 'Stock'].includes(record.po_to_principal_vendor) && <>
                <DetailRow label="Reference Number" value={record.po_to_principal_vendor === 'Yes' ? record.principal_reference_number : record.stock_reference_number} />
                <DetailRow label="Reference Date" value={formatDate(record.po_to_principal_vendor === 'Yes' ? record.principal_reference_date : record.stock_reference_date)} />
              </>}
              <DetailRow label="PI" value={record.pi || 'No'} />
              {record.pi === 'Yes' && <>
                <DetailRow label="PI Reference Number" value={record.pi_reference_number} />
                <DetailRow label="PI Date" value={formatDate(record.pi_reference_date)} />
              </>}
              <DetailRow label="Payment Term" value={record.payment_term} />
              <DetailRow label="Status" value={record.status} />
              {record.status === 'In Transit' && <>
                <DetailRow label="Transporter Name" value={record.transporter_name} />
                <DetailRow label="Transit Reference Number" value={record.transit_reference_number} />
                <DetailRow label="Transit Date" value={formatDate(record.transit_date)} />
                <DetailRow label="Freight" value={formatAmount(record.transit_freight)} />
              </>}
              <DetailRow label="Invoices" value={Array.isArray(record.invoices) && record.invoices.length ? (
                <div>{record.invoices.map(invoice => (
                  <div key={invoice.id}>{invoice.invoice_no} {invoice.invoice_date ? '(' + formatDate(invoice.invoice_date) + ')' : ''}{[invoice.transporter_name, invoice.reference_number].filter(Boolean).length ? ' — ' + [invoice.transporter_name, invoice.reference_number].filter(Boolean).join(' / ') : ''}</div>
                ))}</div>
              ) : null} />
              <DetailRow label="Remarks" value={record.remarks} />
            </div>
            {(canEdit || canDelete) && (
              <div className="quote-detail-footer">
                {canEdit && <Button variant="success" size="sm" disabled={deleting} onClick={() => onEdit(record.id)}>
                  <FontAwesomeIcon icon={faEdit} className="me-2" />Edit
                </Button>}
                {canDelete && <Button variant="danger" size="sm" disabled={deleting} onClick={() => onDelete(record)}>
                  <FontAwesomeIcon icon={faTrash} className="me-2" />Delete
                </Button>}
              </div>
            )}
          </>
        )}
      </div>
      {productsOpen && (
        <aside className="quote-products-panel" aria-label="PO Sales Products">
          <div className="quote-detail-header">
            <div>
              <h5 className="mb-0">PO Products</h5>
              {record.offer_no && <div className="quote-detail-label mt-1">Quote No. {record.offer_no}</div>}
            </div>
            <Button type="button" variant="link" className="p-0 text-dark"
              aria-label="Close products" onClick={() => setShowProducts(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
          <div className="quote-detail-body">
            <QuoteProductsSummary quote={{ ...record, principal: quote.principal, category: quote.category }}
              principalLabel="Principal / Vendor" categoryLabel="Category" showHeading={false} />
          </div>
        </aside>
      )}
      <style>{`
        .quote-detail-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.35); opacity: 0; pointer-events: none; transition: opacity 0.25s ease; z-index: 1040; }
        .quote-detail-backdrop.show { opacity: 1; pointer-events: auto; }
        .quote-detail-panel { position: fixed; top: 0; right: 0; bottom: 0; width: 600px; max-width: 100%; background: #fff; box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15); transform: translateX(100%); transition: transform 0.25s ease; z-index: 1050; display: flex; flex-direction: column; }
        .quote-detail-panel.show { transform: translateX(0); }
        .quote-detail-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e9ecef; }
        .quote-detail-body { flex: 1; overflow-y: auto; padding: 1rem 1.25rem; }
        .quote-detail-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; padding: 0.5rem 0; border-bottom: 1px solid #f1f1f1; }
        .quote-detail-label { color: #6c757d; font-weight: 600; flex-shrink: 0; }
        .quote-detail-value { text-align: right; word-break: break-word; }
        .quote-detail-footer { display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem; padding: 1rem 1.25rem; border-top: 1px solid #e9ecef; flex-wrap: wrap; }
        .quote-detail-footer .btn { flex: 0 0 auto; width: auto !important; min-width: 108px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; }
        @media (max-width: 650px) { .quote-detail-panel { width: 100%; } }
      `}</style>
    </>
  );
}
